import React, {
  useState,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useRef,
} from 'react';
import { Modal, InputNumber, Button, Typography } from 'antd';
import { useBusinessContext } from '../../context/BusinessContext';

export type CurrencyRateDialogInstance = {
  open: (
    actualRate: number,
    sourceCurrency: string,
    receivedCurrency: string,
    sourceAmount: number,
    onSave: (rate: number, sourceCcyAmount: number) => void,
    currentRate?: number,
  ) => void;
};

const CurrencyRateDialog: ForwardRefRenderFunction<
  CurrencyRateDialogInstance,
  {}
> = (_, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [rate, setRate] = useState<number>(1);
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [editedSourceAmount, setEditedSourceAmount] = useState<number>(0);
  const [dialogState, setDialogState] = useState<{
    actualRate: number;
    sourceCurrency: string;
    receivedCurrency: string;
    sourceAmount: number;
    onSave: (rate: number, sourceCcyAmount: number) => void;
  } | null>(null);
  const business = useBusinessContext();

  useImperativeHandle(ref, () => ({
    open: (
      actualRate,
      sourceCurrency,
      receivedCurrency,
      sourceAmount,
      onSave,
      currentRate,
    ) => {
      setDialogState({
        actualRate,
        sourceCurrency,
        receivedCurrency,
        sourceAmount,
        onSave,
      });
      setRate(currentRate ?? actualRate ?? 1);
      setEditedSourceAmount(sourceAmount);
      setReceivedAmount(sourceAmount * (currentRate ?? actualRate ?? 1));
      setVisible(true);
    },
  }));

  const handleRateChange = (value: number | null) => {
    setRate(value ?? 0);
    setReceivedAmount(editedSourceAmount * (value ?? 0));
  };

  const handleReceivedAmountChange = (value: number | null) => {
    setReceivedAmount(value ?? 0);
    if (editedSourceAmount !== 0) {
      setRate((value ?? 0) / editedSourceAmount);
    }
  };

  const handleSourceAmountChange = (value: number | null) => {
    setEditedSourceAmount(value ?? 0);
    setReceivedAmount((value ?? 0) * rate);
  };

  const handleReset = async () => {
    if (!dialogState) return;
    const fallbackRate =
      (
        await business.getCurrencyRate(
          dialogState.sourceCurrency,
          dialogState.receivedCurrency,
        )
      )?.rate ??
      dialogState.actualRate ??
      1;
    setRate(fallbackRate);
    setEditedSourceAmount(dialogState.sourceAmount);
    setReceivedAmount(dialogState.sourceAmount * fallbackRate);
  };

  const handleDone = () => {
    if (dialogState) {
      dialogState.onSave(rate, editedSourceAmount);
    }
    setVisible(false);
  };

  const isDoneDisabled =
    editedSourceAmount <= 0 || rate <= 0 || receivedAmount <= 0;

  const labelWidth = 110;

  return (
    <Modal
      title="Edit Currency Rate"
      open={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      centered
      width={400}
    >
      {dialogState && (
        <>
          <div
            style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
          >
            <table style={{ width: '100%' }}>
              <tr style={{ paddingBottom: 10 }}>
                <td style={{ width: labelWidth }}>
                  <Typography.Text strong>
                    {dialogState.sourceCurrency} Amount:
                  </Typography.Text>
                </td>
                <td>
                  <InputNumber
                    precision={2}
                    min={0.01}
                    value={editedSourceAmount}
                    style={{ width: '100%' }}
                    onChange={handleSourceAmountChange}
                    onPressEnter={handleDone}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <div style={{ width: labelWidth }}>
                    <Typography.Text strong>Exchange Rate:</Typography.Text>
                  </div>
                </td>
                <td>
                  <InputNumber
                    min={0.01}
                    value={rate}
                    style={{ width: '100%' }}
                    onChange={handleRateChange}
                    onPressEnter={handleDone}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <Typography.Text strong>
                    {dialogState.receivedCurrency} Amount:
                  </Typography.Text>
                </td>
                <td>
                  <InputNumber
                    precision={2}
                    min={0.01}
                    value={receivedAmount}
                    style={{ width: '100%' }}
                    onChange={handleReceivedAmountChange}
                    onPressEnter={handleDone}
                  />
                </td>
              </tr>
            </table>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 20,
            }}
          >
            <Button onClick={handleReset}>Reset</Button>
            <Button
              type="primary"
              onClick={handleDone}
              disabled={isDoneDisabled}
            >
              Done
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export const useCurrencyRateDialog = () =>
  useRef<CurrencyRateDialogInstance>(null);

export default React.forwardRef<CurrencyRateDialogInstance, {}>(
  CurrencyRateDialog,
);
